import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { useQuery } from "@tanstack/react-query"
import { getTicket } from "services/tickets"
import TableProduct from "components/sales/ticket"
import bwipjs from "bwip-js"
import { API } from "config/API"
import { lenguage, dateConfig } from "config/format-date"

const RenderTicketContent = React.forwardRef(({ data, businessInfo }, ref) => {
  const canvasRef = useRef(null)
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000"
  const qrData = `{
        "id": "${data?.id}", 
        "url": "${API_URL}/sales/${data?.id}",
        "user": {"id": "${data?.user?.id}", "name": "${data?.user?.first_name} ${data?.user?.last_name}"},
        "customer": {"id": "${data?.customer?.id}", "name": "${data?.customer?.name}"}, 
        "date": "${data?.date}",
        "subtotal": ${data?.subtotal},
        "total": ${data?.total}
    }`

  useEffect(() => {
    if (canvasRef.current) {
      bwipjs.toCanvas(canvasRef.current, {
        bcid: "qrcode",
        scale: 1,
        text: qrData,
      })
    }
  }, [data.id, qrData])

  const dataTicket = useQuery(["invoice", data?.id], () => getTicket(data.id), {
    enabled: Boolean(data?.descriptions && data?.id),
  })

  const ticketData = dataTicket.data || data
  const totalQuantity = ticketData?.products?.reduce((total, product) => total + Number(product?.quantity || 0), 0)
  const customerName = ticketData?.customer?.name || "Cliente"
  const cashierName =
    [ticketData?.user?.first_name, ticketData?.user?.last_name].filter(Boolean).join(" ") || "Cajero"

  function currencyFormatter({ currency, value }) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      minimumFractionDigits: 2,
      currency,
    })
    return formatter.format(Number(value || 0))
  }

  const apiUrl = ["https://api.pos.emkode.org", "https://api-dulceria.emkode.com", "https://api-plasticos.emkode.com"]
  const getImage = (item) => {
    const imageUrl = item.image?.medium_thumbnail || item.image?.thumbnail || item.image?.url || item.image
    if (!imageUrl) return ""

    if (apiUrl.includes(API.defaults.baseURL)) {
      const imageArray = imageUrl.split("media")
      if (imageArray && imageArray[0].slice(-1) !== ".") {
        return imageArray[0] + ".media" + imageArray[1]
      }
    }

    return imageUrl
  }

  const date = new Date(String(ticketData.date || "").replace("Z", ""))
  const formattedDate = date.toLocaleString(lenguage, dateConfig)

  const ticketStyle = {
    padding: "20px",
    color: "black",
  }

  const paragraphStyle = {
    color: "black",
    lineHeight: "11px",
    fontSize: "11px",
    margin: 0,
  }

  const headerTextStyle = {
    color: "black",
    fontSize: "11px",
    lineHeight: "11px",
    textTransform: "uppercase",
    margin: 0,
    textAlign: "center",
  }

  const logoStyle = {
    width: "50%",
    margin: "0 auto",
    display: "block",
  }

  const headerRowsStyle = {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: "2px",
    marginBottom: "2px",
    gap: "8px",
  }

  return (
    <div ref={ref} className="ticket-print-area" style={ticketStyle}>
      {businessInfo.data?.results?.slice(0, 1).map((business) => {
        const address = [business.street, business.neighborhood].filter(Boolean).join(" ")

        return (
        <React.Fragment key={business.id}>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <img src={getImage(business)} alt="Logo" style={logoStyle} />
          </div>

          <h2 style={headerTextStyle}>{business.name}</h2>
          <p style={headerTextStyle}>{address}</p>
          <p style={headerTextStyle}>
            C.P. {business.fiscal_zipcode || business.fiscal_zip_code || business.zip_code}
          </p>
          <p style={headerTextStyle}>TELEFONO: {business.phone_number}</p>
          <p style={{ borderBottom: "1px solid black", margin: "5px 0" }} />
        </React.Fragment>
        )
      })}

      <div style={headerRowsStyle}>
        <p style={paragraphStyle}>
          Caja: <b>{ticketData?.cash_register || "Caja"}</b>
        </p>
        <p style={paragraphStyle}>
          Ticket:{" "}
          <b>
            {ticketData?.is_pending
              ? `C.C. ${ticketData?.folio || ticketData?.id}`
              : ticketData?.folio || ticketData?.id}
          </b>
        </p>
      </div>

      <div style={headerRowsStyle}>
        <p style={paragraphStyle}>
          <b>{cashierName}</b>
        </p>
        <p style={paragraphStyle}>{formattedDate}</p>
      </div>

      <p style={{ borderBottom: "1px solid black", width: "100%", margin: "5px 0" }} />

      <div style={{ ...headerRowsStyle, justifyContent: "flex-end" }}>
        <p style={{ ...paragraphStyle, fontSize: "13px", marginBottom: "5px" }}>{customerName}</p>
      </div>

      <TableProduct products={ticketData?.products} />

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          gap: "35px",
          marginTop: "8px",
        }}
      >
        <div>
          <h3 style={{ color: "black", margin: 0, textAlign: "center" }}>TOTAL</h3>
          <h2 style={{ color: "black", margin: 0, textAlign: "center" }}>
            {currencyFormatter({ currency: "USD", value: ticketData?.total })}
          </h2>
        </div>
        <div style={{ fontSize: "15px" }}>
          <p style={{ color: "black", lineHeight: "15px", margin: 0, textAlign: "center" }}>SUBTOTAL</p>
          <p style={{ color: "black", lineHeight: "15px", margin: 0, textAlign: "center" }}>
            {currencyFormatter({ currency: "USD", value: ticketData?.subtotal })}
          </p>
          <p style={{ color: "black", lineHeight: "15px", margin: 0, textAlign: "center" }}>DESCUENTO</p>
          <p style={{ color: "black", lineHeight: "15px", margin: 0, textAlign: "center" }}>
            {currencyFormatter({ currency: "USD", value: ticketData?.discount })}
          </p>
        </div>
      </div>

      <p style={{ ...headerTextStyle, fontSize: "11px", textAlign: "left", marginTop: "8px" }}>
        {ticketData?.total_string || ticketData?.total_text || ""}
      </p>
      {/* <canvas ref={canvasRef}></canvas> */}
      <h4
        style={{
          color: "black",
          display: "flex",
          justifyContent: "center",
          margin: "5px 0",
          fontSize: "13px",
        }}
      >
        Total de Articulos: {totalQuantity}
      </h4>

      <p
        style={{
          ...headerTextStyle,
          textAlign: "center",
          fontSize: "11px",
          lineHeight: "17px",
          marginTop: "4px",
        }}
      >
        Gracias por su preferencia.
        <br />
        Este ticket no es un comprobante fiscal.
        <br />
        Si requiere factura, solicitela el mismo dia.
      </p>
      <p style={{ color: "black", textAlign: "center", fontSize: "11px", margin: 0 }}>
        RFC: {businessInfo.data?.results?.[0]?.rfc}
      </p>
    </div>
  )
})

RenderTicketContent.propTypes = {
  data: PropTypes.object.isRequired,
  businessInfo: PropTypes.object.isRequired,
}

RenderTicketContent.displayName = "RenderTicketContent"

export default RenderTicketContent
